import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import * as models from 'app/models';
import * as services from 'app/services';
import { toLocalPoints, isMappedOn, DEFAULT_ZONE_COLOR } from './zone-map.util';

// A drawable shape resolved into image-local SVG geometry, ready to bind.
interface RenderShape {
  zoneId: string;
  name: string;
  type: models.ZoneShapeType;
  color: string;
  // rect
  x?: number; y?: number; w?: number; h?: number;
  // polygon
  pointsAttr?: string;
  // pin
  cx?: number; cy?: number; r?: number;
  labelX: number;
  labelY: number;
}

interface RenderElement {
  id: string;
  type: 'line' | 'label';
  pointsAttr?: string;
  x?: number;
  y?: number;
  text?: string;
}

// Lightweight, dependency-free (no Konva) read-only renderer for zone shapes on
// a floor plan. Reused wherever zones are *consumed* — zone assignment, sensor
// location, harvest "where is this growing" — so those surfaces stay cheap.
//
// Inputs: a FloorPlan + the full zones list. Optionally interactive: highlights
// `selectedIds` and emits zoneClick on tap.
@Component({
  selector: 'app-zone-map-viewer',
  templateUrl: './zone-map-viewer.component.html',
  styleUrls: ['./zone-map-viewer.component.scss'],
  standalone: false
})
export class ZoneMapViewerComponent implements OnChanges {

  @Input() floorPlan: models.FloorPlan;
  @Input() zones: models.Zone[] = [];
  @Input() interactive: boolean = false;
  @Input() selectedIds: string[] = [];
  @Input() highlightZoneId: string;

  // Optional allocation overlay, keyed by zone id. Lets a consumer tint shapes
  // by how full they are and print a count on them, so the map can carry the
  // same numbers as a list instead of being a bare picker. Absent = plain map.
  @Input() fillStates: { [zoneId: string]: 'empty' | 'partial' | 'full' | 'over' } = {};
  @Input() badges: { [zoneId: string]: string } = {};

  @Output() zoneClick = new EventEmitter<string>();

  imageUrl: string;
  viewW: number = 1000;
  viewH: number = 1000;
  shapes: RenderShape[] = [];
  elements: RenderElement[] = [];

  constructor(private floorPlanService: services.FloorPlanService) { }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['floorPlan']) {
      this.viewW = this.floorPlan?.canvasWidth || this.floorPlan?.imageWidth || 1000;
      this.viewH = this.floorPlan?.canvasHeight || this.floorPlan?.imageHeight || 1000;
      this.imageUrl = await this.floorPlanService.getImageUrl(this.floorPlan);
    }
    this.build();
  }

  get pinRadius(): number { return Math.max(this.viewW, this.viewH) * 0.012; }

  isSelected(zoneId: string): boolean {
    return this.selectedIds && this.selectedIds.indexOf(zoneId) > -1;
  }

  fillState(zoneId: string): string {
    return this.fillStates ? this.fillStates[zoneId] : null;
  }

  badge(zoneId: string): string {
    return this.badges ? this.badges[zoneId] : null;
  }

  onClick(zoneId: string) {
    if (this.interactive) this.zoneClick.emit(zoneId);
  }

  private build() {
    let out: RenderShape[] = [];
    if (this.floorPlan) {
      this.walk(this.zones || [], z => {
        if (!isMappedOn(z, this.floorPlan.id)) return;
        out.push(this.toRender(z));
      });
    }
    this.shapes = out;
    this.elements = (this.floorPlan?.elements || []).map(element => ({
      id: element.id,
      type: element.type,
      pointsAttr: element.type === 'line'
        ? this.pointsToAttr(toLocalPoints(element.points || [], this.viewW, this.viewH))
        : null,
      x: (element.x || 0) * this.viewW,
      y: (element.y || 0) * this.viewH,
      text: element.text,
    }));
  }

  private toRender(zone: models.Zone): RenderShape {
    let s = zone.mapData.shape;
    let color = zone.mapData.color || DEFAULT_ZONE_COLOR;
    let local = toLocalPoints(s.points, this.viewW, this.viewH);
    let base: RenderShape = { zoneId: zone.id, name: zone.name, type: s.type, color, labelX: 0, labelY: 0 };

    if (s.type === 'rect') {
      base.x = local[0]; base.y = local[1]; base.w = local[2]; base.h = local[3];
      base.labelX = base.x + base.w / 2;
      base.labelY = base.y + base.h / 2;
    } else if (s.type === 'pin') {
      base.cx = local[0]; base.cy = local[1]; base.r = this.pinRadius;
      base.labelX = base.cx; base.labelY = base.cy - base.r - 4;
    } else {
      base.pointsAttr = this.pointsToAttr(local);
      let xs: number[] = [], ys: number[] = [];
      for (let i = 0; i < local.length; i += 2) { xs.push(local[i]); ys.push(local[i + 1]); }
      base.labelX = xs.reduce((a, b) => a + b, 0) / xs.length;
      base.labelY = ys.reduce((a, b) => a + b, 0) / ys.length;
    }
    return base;
  }

  private pointsToAttr(local: number[]): string {
    let pairs: string[] = [];
    for (let i = 0; i < local.length; i += 2) pairs.push(`${local[i]},${local[i + 1]}`);
    return pairs.join(' ');
  }

  private walk(list: models.Zone[], fn: (z: models.Zone) => void) {
    (list || []).forEach(z => { fn(z); if (z.zones) this.walk(z.zones, fn); });
  }
}
